import { Link } from "react-router-dom";
import {
  Mail,
  MapPin,
  Linkedin,
  ExternalLink,
} from "lucide-react";
import SiteHealthPill from "./SiteHealthPill";

const PRIMARY_NAV_LINKS = [
  { to: "/vacancies", label: "Live Vacancies" },
  { to: "/depot-finder", label: "Vacancy Map" },
  { to: "/operators", label: "Companies Hiring Now" },
  { to: "/careers", label: "Role Guides" },
  { to: "/application-resources", label: "For Candidates" },
  { to: "/employers", label: "For Employers" },
  { to: "/faqs", label: "FAQs" },
];

const PARTNER_SITES = [
  {
    name: "UKTDA",
    url: "http://traindriveracademy.com/",
  },
  {
    name: "Train Driver Foundation",
    url: "https://www.traindriverfoundation.com/",
  },
  {
    name: "Train Driver Psychometrics",
    url: "https://traindriverpsychometrics.co.uk/",
  },
];

export default function Footer() {
  return (
    <footer className="bg-deep text-ice mt-24 border-t border-line relative overflow-hidden">
      <div className="absolute inset-0 grid-bg opacity-30 pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-20 grid md:grid-cols-12 gap-10">
        {/* Brand */}
        <div className="md:col-span-4">
          <div className="flex items-center gap-3 mb-6">
            <img
              src="/logo.png"
              alt="Railway Careers UK"
              className="w-10 h-10 rounded-full"
            />

            <div className="font-display font-bold text-ice text-lg tracking-[-0.012em]">
              Railway Careers UK
            </div>
          </div>

          <p className="text-[14px] text-slate leading-relaxed max-w-sm">
            Independent UK railway recruitment platform — live vacancies, role
            guides, postcode-based depot finder and the weekly Monday Briefing.
          </p>

          <div className="mt-8">
            <div className="text-[11px] uppercase tracking-[0.16em] text-sky font-semibold mb-4">
              Primary navigation
            </div>

            <ul className="space-y-3 text-[14px] text-slate">
              {PRIMARY_NAV_LINKS.slice(0, 4).map((item) => (
                <li key={item.to}>
                  <Link
                    to={item.to}
                    className="hover:text-ice"
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Candidates */}
        <div className="md:col-span-2">
          <div className="text-[11px] uppercase tracking-[0.16em] text-sky font-semibold mb-5">
            For Candidates
          </div>

          <ul className="space-y-3 text-[14px] text-slate">
            <li>
              <Link
                to="/application-resources"
                className="hover:text-ice"
              >
                Candidate Toolkit
              </Link>
            </li>

            <li>
              <Link
                to="/train-driver-jobs"
                className="hover:text-ice"
              >
                Train driver jobs UK
              </Link>
            </li>

            <li>
              <Link
                to="/about"
                className="hover:text-ice"
              >
                About
              </Link>
            </li>

            <li>
              <a
                href="/api/feed.xml"
                className="hover:text-ice"
              >
                RSS feed
              </a>
            </li>
          </ul>
        </div>

        {/* Employers */}
        <div className="md:col-span-2">
          <div className="text-[11px] uppercase tracking-[0.16em] text-sky font-semibold mb-5">
            Employers
          </div>

          <ul className="space-y-3 text-[14px] text-slate">
            <li>
              <Link
                to="/employers"
                className="hover:text-ice"
              >
                Advertise vacancies
              </Link>
            </li>

            <li>
              <Link
                to="/employer-portal/register?plan=free"
                className="hover:text-ice"
              >
                Post for free
              </Link>
            </li>

            <li>
              <Link
                to="/employer-portal/register"
                className="hover:text-ice"
              >
                Create account
              </Link>
            </li>

            <li>
              <Link
                to="/employer-portal/login"
                className="hover:text-ice"
              >
                Sign in
              </Link>
            </li>
          </ul>
        </div>

        {/* Partner Sites */}
        <div className="md:col-span-2">
          <div className="text-[11px] uppercase tracking-[0.16em] text-sky font-semibold mb-5">
            Partner Sites
          </div>

          <ul className="space-y-3 text-[14px] text-slate">
            {PARTNER_SITES.map((site) => (
              <li key={site.url}>
                <a
                  href={site.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-ice inline-flex items-center gap-1.5"
                >
                  {site.name}
                  <ExternalLink className="w-3 h-3" />
                </a>
              </li>
            ))}
          </ul>
        </div>

        {/* Contact */}
        <div className="md:col-span-2">
          <div className="text-[11px] uppercase tracking-[0.16em] text-sky font-semibold mb-5">
            Get in Touch
          </div>

          <div className="space-y-4 text-[14px] text-slate">
            <div className="flex items-center gap-3">
              <MapPin className="w-4 h-4 text-sky" />
              Manchester, United Kingdom
            </div>

            <a
              href="mailto:contact@railwaycareers.co.uk"
              aria-label="Email Railway Careers UK"
              title="contact@railwaycareers.co.uk"
              className="inline-flex items-center gap-3 hover:text-ice transition-colors"
              data-testid="footer-email"
            >
              <Mail className="w-4 h-4 text-sky" />
              <span>email</span>
            </a>

            <a
              href="https://www.linkedin.com/company/railway-careers/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Railway Careers UK on LinkedIn"
              title="Railway Careers UK on LinkedIn"
              className="inline-flex items-center gap-3 hover:text-ice transition-colors"
              data-testid="footer-linkedin"
            >
              <Linkedin className="w-4 h-4 text-sky" />
              <span>LinkedIn</span>
            </a>
          </div>

          <Link
            to="/admin/login"
            className="text-[10px] uppercase tracking-[0.16em] text-slate/40 hover:text-sky mt-8 inline-block"
            data-testid="footer-admin-link"
          >
            Admin
          </Link>
        </div>
      </div>

      {/* Bottom strip */}
      <div className="relative border-t border-line">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-6">
          <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-5">
            {/* Copyright + Web Wizard */}
            <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-5">
              <div className="text-[12px] text-slate">
                © {new Date().getFullYear()} UK Train Driver Academy Ltd
                (UKTDA) | Railway Careers UK | All rights reserved
              </div>

              <span className="hidden md:block text-slate/30">
                |
              </span>

              <a
                href="https://thewebdesignwizard.co.uk/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Website by The Web Design Wizard"
                className="inline-flex items-center gap-2.5 group w-fit"
              >
                <img
                  src="https://blackrodnow.com/webwizard.png"
                  alt="The Web Design Wizard"
                  className="h-10 w-auto object-contain transition-transform group-hover:scale-105"
                  loading="lazy"
                />

                <span className="flex flex-col leading-tight">
                  <span className="text-[9px] text-slate/60">
                    Website by
                  </span>

                  <span className="text-[11px] font-semibold text-slate group-hover:text-ice transition-colors">
                    The Web Design Wizard
                  </span>

                  <span className="text-[9px] text-sky group-hover:underline">
                    thewebdesignwizard.co.uk
                  </span>
                </span>
              </a>
            </div>

            {/* Status / legal */}
            <div className="flex flex-wrap items-center gap-6 text-[12px] text-slate">
              <SiteHealthPill />

              <Link
                to="/privacy"
                className="hover:text-ice"
              >
                Privacy
              </Link>

              <Link
                to="/terms"
                className="hover:text-ice"
              >
                Terms
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}